import {useContext, useEffect, useRef, useState} from "react";
import {SocketContext} from "@/contexts/socketContext";
import {PageBaseContext} from "@/contexts/pageBaseContext";
import {useTeamProgressContext} from "@/contexts/TeamProgressContext";
import {TeamProgressWidget} from "@/enums";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import useCustomSnackbar from "@/snackbars/useCustomSnackbar";
import dayjs from "dayjs";
import {GridLinkOperator} from "@mui/x-data-grid-pro";
import {IEntity, IEntityFilter, SOCKET_EVENTS, StartTeamProgressExportDataPayload, JobStatusCallback} from "@buildwitt/shapeshift-types";
import {useApi} from "@buildwitt/shapeshift-identity";
import fileDownload from "js-file-download";
import {useExportTeamProgressContext} from "@/contexts/ExportTeamProgressContext";

export const useManageTeamProgressReport = <T extends IEntity>(reportType: TeamProgressWidget) => {
	const {t} = useSSTranslation();
	const socket = useContext(SocketContext)
	const [pageBaseContext] = useContext(PageBaseContext)
	const {successSnackbar, errorSnackbar, processInProgressSnackbar, closeSnackbar} = useCustomSnackbar()
	const {filters} = useTeamProgressContext();
	const {addExportTask, exportTaskExists, exportTasksRunning, removeExportTask} = useExportTeamProgressContext();
	const [dataGridFilter, setDataGridFilter] = useState<{ operator?: GridLinkOperator, conditions?: IEntityFilter<T>[] }>();
	const [dataGridSort, setDataGridSort] = useState<{ order: string, orderBy?: string }>({order: "asc"});

	// This function is temporary and used only for QA testing. The function to initiate report downloads is "initDownload".
	const downloadReport = () => {
		if (exportTaskExists(reportType)) return;
		if (exportTasksRunning()){
			errorSnackbar(t("team_progress_one_report_a_time_msg", "You can only download one report at a time"), true);
			return;
		}

		addExportTask(reportType);
		const key = processInProgressSnackbar(t("team_progress_generating_your_report_msg", "Generating your report… Don't close or refresh this window."), true, true);
		setTimeout(() => {
			removeExportTask(reportType);
			closeSnackbar(key);
			successSnackbar(t("team_progress_download_report_has_started", "Your download has started"));
		}, 25000);
	}

	const initDownload = () => {
		if (exportTaskExists(reportType)) return;
		if (exportTasksRunning()){
			errorSnackbar(t("team_progress_one_report_a_time_msg", "You can only download one report at a time"), true);
			return;
		}

		if (!socket){
			errorSnackbar(t("team_progress_download_export_failed", "Export failed. Please try again."));
			return;
		}

		const cohortId = pageBaseContext.memberedCohort.id;
		const {archivedUsers, dateFrom, dateTo} = filters;
		const dataGridFilters = dataGridFilter ? {
			operator: dataGridFilter.operator,
			conditions: dataGridFilter.conditions
		} : null;
		const {order, orderBy} = dataGridSort;

		addExportTask(reportType);

		const payload: StartTeamProgressExportDataPayload = {
			cohortId,
			archivedUsers,
			dateFrom: dayjs(dateFrom).format("YYYY-MM-DD"),
			dateTo: dayjs(dateTo).format("YYYY-MM-DD"),
			filters: dataGridFilters,
			order,
			orderBy,
			reportType
		};

		socket.emit(SOCKET_EVENTS.START_TEAM_PROGRESS_EXPORT_DATA, payload,
		(response: JobStatusCallback) => {
			if (response.inProgress){
				processInProgressSnackbar(t("team_progress_generating_your_report_msg", "Generating your report… Don't close or refresh this window."), true, true);
			} else {
				removeExportTask(reportType);
				errorSnackbar(t("team_progress_download_export_failed", "Export failed. Please try again."));
			}
		});
	}

	const handleUpdatedFilter = (operator: GridLinkOperator, conditions: IEntityFilter<T>[]) => {
		setDataGridFilter({
			operator,
			conditions
		});
	}

	const handleUpdatedSort = (order: string, orderBy: string) => {
		setDataGridSort({
			order,
			orderBy
		});
	}

	return {
		downloadReport,
		initDownload,
		handleUpdatedFilter,
		handleUpdatedSort
	}
}

export const useDownloadReport = () => {
	const {t} = useSSTranslation();
	const [{isLoading, data, isError} ,getCsvFile] = useApi();
	const {successSnackbar, errorSnackbar, closeSnackbar} = useCustomSnackbar();
	const fileNameRef = useRef(null);

	useEffect(() => {
		if (isError && !isLoading){
			closeSnackbar();
			fileNameRef.current = null;
			errorSnackbar(t("team_progress_download_export_failed", "Export failed. Please try again."));
		}

		if (!isError && !isLoading && data !== undefined){
			const fileName = fileNameRef.current;
			fileDownload(data as Blob, fileName);
			fileNameRef.current = null;
		}
	}, [data, isLoading, isError])

	const endExportBackendProcess = (isSuccessful: boolean, fileName: string) => {
		fileNameRef.current = fileName;
		if (!isSuccessful) {
			closeSnackbar();
			errorSnackbar(t("team_progress_download_export_failed", "Export failed. Please try again."));
			return;
		}

		getCsvFile({
			method: "GET",
			url: "/api/v1/blob",
			config: {
				params: {
					containerName: "team-progress-reports",
					blobName: fileName,
					mimeType: "text/csv"
				}
			}
		})
		closeSnackbar();
		successSnackbar(t("team_progress_download_report_has_started", "Your download has started"));
	}

	return {
		endExportBackendProcess
	}
}